import React from 'react';
import { Abteilung, Projekt } from '../types';
import { cn } from '../lib/utils';
import { Filter, Users, Briefcase, Check, ChevronRight, X } from 'lucide-react';
import { Button } from './ui/Button';
interface SidebarProps {
  allAbteilungen: Abteilung[];
  selectedAbteilungen: Abteilung[];
  onToggleAbteilung: (abt: Abteilung) => void;
  projekte: Projekt[];
  selectedProjektId: string | undefined;
  onSelectProjekt: (id: string | undefined) => void;
  isOpen?: boolean;
  onClose?: () => void;
}
export function Sidebar({
  allAbteilungen,
  selectedAbteilungen,
  onToggleAbteilung,
  projekte,
  selectedProjektId,
  onSelectProjekt,
  isOpen = false,
  onClose
}: SidebarProps) {
  return <>
      {/* Mobile Overlay */}
      {isOpen && <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden animate-in fade-in" onClick={onClose} data-id="element-366" />}

      <div className={cn('w-72 flex-shrink-0 flex flex-col bg-background/95 backdrop-blur-sm border-r h-full overflow-y-auto custom-scrollbar transition-transform duration-300 ease-in-out z-50', 'fixed inset-y-0 left-0 lg:relative lg:translate-x-0', isOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:shadow-none')} data-id="element-367">
        <div className="p-5 border-b bg-background/50 sticky top-0 z-10 flex items-center justify-between" data-id="element-368">
          <div className="flex items-center gap-2 text-foreground" data-id="element-369">
            <Filter className="h-4 w-4 text-primary" data-id="element-370" />
            <h2 className="font-semibold text-sm tracking-wide uppercase text-muted-foreground" data-id="element-371">
              Filter & Ansicht
            </h2>
          </div>
          {onClose && <Button variant="ghost" size="icon" className="h-6 w-6 lg:hidden" onClick={onClose} data-id="element-372">
              <X className="h-4 w-4" data-id="element-373" />
            </Button>}
        </div>

        <div className="p-5 space-y-8 pb-20" data-id="element-374">
          {/* Departments Filter */}
          <div className="space-y-3" data-id="element-375">
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-376">
              <Users className="h-3.5 w-3.5" data-id="element-377" />
              Abteilungen
            </h3>
            <div className="space-y-1" data-id="element-378">
              {allAbteilungen.map(abt => {
              const isSelected = selectedAbteilungen.includes(abt);
              return <label key={abt} className={cn('flex items-center gap-3 text-sm px-3 py-2 rounded-md cursor-pointer transition-all duration-200 group', isSelected ? 'bg-primary/5 text-foreground font-medium' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground')} data-id="element-379">
                    <div className={cn('w-4 h-4 rounded border flex items-center justify-center transition-colors', isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/30 group-hover:border-primary/50')} data-id="element-380">
                      {isSelected && <Check className="h-3 w-3" data-id="element-381" />}
                    </div>
                    <input type="checkbox" className="hidden" checked={isSelected} onChange={() => onToggleAbteilung(abt)} data-id="element-382" />
                    {abt}
                  </label>;
            })}
            </div>
          </div>

          <div className="h-px bg-border/50" data-id="element-383" />

          {/* Project Filter */}
          <div className="space-y-3" data-id="element-384">
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-385">
              <Briefcase className="h-3.5 w-3.5" data-id="element-386" />
              Projekte
            </h3>
            <div className="space-y-1" data-id="element-387">
              <button onClick={() => onSelectProjekt(undefined)} className={cn('w-full flex items-center gap-3 text-sm px-3 py-2 rounded-md transition-all duration-200 text-left', !selectedProjektId ? 'bg-primary/5 text-foreground font-medium shadow-sm' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground')} data-id="element-388">
                <div className="w-1.5 h-1.5 rounded-full bg-foreground/50" data-id="element-389" />
                Alle Projekte anzeigen
              </button>

              {projekte.map(p => {
              const isSelected = selectedProjektId === p.id;
              return <button key={p.id} onClick={() => onSelectProjekt(isSelected ? undefined : p.id)} className={cn('w-full flex items-center gap-3 text-sm px-3 py-2 rounded-md transition-all duration-200 text-left group', isSelected ? 'bg-background shadow-sm ring-1 ring-border text-foreground font-medium' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground')} data-id="element-390">
                    <div className={cn('w-2 h-2 rounded-full shadow-sm transition-transform group-hover:scale-110', isSelected && 'scale-125')} style={{
                  backgroundColor: p.farbe
                }} data-id="element-391" />
                    <span className="truncate" data-id="element-392">{p.projektnummer}</span>
                    {isSelected && <ChevronRight className="h-3 w-3 ml-auto text-muted-foreground" data-id="element-393" />}
                  </button>;
            })}
            </div>
          </div>

          <div className="h-px bg-border/50" data-id="element-394" />

          {/* Legend */}
          <div className="space-y-3" data-id="element-395">
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider" data-id="element-396">
              Legende
            </h3>
            <div className="space-y-2.5 text-xs text-muted-foreground" data-id="element-397">
              <div className="flex items-center gap-3" data-id="element-398">
                <div className="w-8 h-4 rounded bg-red-50 dark:bg-red-950 border border-red-100 dark:border-red-900 bg-stripes-red" data-id="element-399"></div>
                <span data-id="element-400">Feiertag</span>
              </div>
              <div className="flex items-center gap-3" data-id="element-401">
                <div className="w-8 h-4 rounded bg-muted/30 border border-border bg-stripes-gray" data-id="element-402"></div>
                <span data-id="element-403">Abwesend</span>
              </div>
              <div className="flex items-center gap-3" data-id="element-404">
                <div className="w-8 h-4 rounded border-2 border-destructive bg-destructive/5 flex items-center justify-center" data-id="element-405">
                  <div className="w-1 h-1 bg-destructive rounded-full animate-pulse" data-id="element-406" />
                </div>
                <span data-id="element-407">Konflikt / Überbuchung</span>
              </div>
              <div className="flex items-center gap-3" data-id="element-408">
                <div className="w-8 h-4 rounded bg-primary/10 border-l-2 border-primary" data-id="element-409"></div>
                <span data-id="element-410">Aktuelle Woche</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
}